import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Check, Undo, Loader2, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function DocGapItem({ gap, onUpdate }) {
    const { t } = useTranslation();
    const [notes, setNotes] = useState(gap.notes || "");
    const { call: updateGap, isLoading } = useBluelagoon({ method: "updateDocGap" });

    const handleResolve = async (resolved) => {
        const res = await updateGap({ id: gap.id, resolved, notes }).catch(() => null);
        if (res?.status === 200) {
            onUpdate(res.data.docGap);
            toast.success(resolved
                ? t("admin.support.gapResolved", "Gap marked as resolved")
                : t("admin.support.gapUnresolved", "Gap marked as unresolved"));
        }
    };

    const handleNotesBlur = async () => {
        if (notes !== (gap.notes || "")) {
            const res = await updateGap({ id: gap.id, notes }).catch(() => null);
            if (res?.status === 200) onUpdate(res.data.docGap);
        }
    };

    return (
        <div className="flex items-center gap-3 p-3 border rounded-lg">
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{gap.query}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-xs">{gap.locale}</Badge>
                    <span>{format(new Date(gap.createdAt), "MMM d, yyyy")}</span>
                    {gap.ticket && (
                        <a href={`/admin/support/${gap.ticket.id}`} className="flex items-center gap-0.5 text-blue-600 hover:underline">
                            <ExternalLink className="w-3 h-3" />{gap.ticket.subject?.slice(0, 30)}
                        </a>
                    )}
                </div>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={handleNotesBlur}
                    placeholder={t("admin.support.addNotes", "Add notes...")} className="mt-2 text-xs h-7" />
            </div>
            <div className="flex-shrink-0">
                {gap.resolvedAt ? (
                    <Button variant="outline" size="sm" onClick={() => handleResolve(false)} disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo className="w-4 h-4" />}
                    </Button>
                ) : (
                    <Button variant="default" size="sm" onClick={() => handleResolve(true)} disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </Button>
                )}
            </div>
        </div>
    );
}
